import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { OnboardingCheck } from '@/components/auth/OnboardingCheck';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { CognitoAuthProvider, useCognitoAuth } from '@/contexts/AuthContext';
import { OrganizationProvider } from '@/hooks/useOrganization';
import { AcceptInvitation } from '@/pages/AcceptInvitation';
import { AccountDetail } from '@/pages/AccountDetail';
import { Accounts } from '@/pages/Accounts';
import { AuditLog } from '@/pages/AuditLog';
import { BalanceSheet } from '@/pages/BalanceSheet';
import { BankAccounts } from '@/pages/BankAccounts';
import { Dashboard } from '@/pages/Dashboard';
import { EmailVerificationPage } from '@/pages/EmailVerificationPage';
import { FinancialSettings } from '@/pages/FinancialSettings';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { GFICodes } from '@/pages/GFICodes';
import { IntegrationSettings } from '@/pages/IntegrationSettings';
import { Inventory } from '@/pages/Inventory';
import { InventoryValuationPage } from '@/pages/InventoryValuation';
import { Journal } from '@/pages/Journal';
import { LandingPage } from '@/pages/LandingPage';
import { LoginPage } from '@/pages/LoginPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { OrganizationMembers } from '@/pages/OrganizationMembers';
import { OrganizationSettings } from '@/pages/OrganizationSettings';
import { ProfitLoss } from '@/pages/ProfitLoss';
import { Reconciliation } from '@/pages/Reconciliation';
import { RegistrationPage } from '@/pages/RegistrationPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { TaxCodes } from '@/pages/TaxCodes';
import { TaxSummary } from '@/pages/TaxSummary';
import { Transactions } from '@/pages/Transactions';
import { TrialBalancePage } from '@/pages/TrialBalance';
import { UserSettings } from '@/pages/UserSettings';

function AppRoutes() {
  const { user, loading } = useCognitoAuth();

  console.log('[AppRoutes] Rendering, loading:', loading, 'user:', user?.email);

  return (
    <Routes>
      {/* Public routes — render immediately, auth resolves in background */}
      <Route path="/" element={user ? <Navigate to="/app" replace /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/app" replace /> : <RegistrationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/onboarding/organization" element={<OnboardingPage />} />
      <Route path="/invite/:token" element={<AcceptInvitation />} />
      
      {/* Protected routes — show loading spinner while auth resolves */}
      <Route
        path="/app"
        element={
          loading ? null : (
            <ProtectedRoute>
              <OnboardingCheck>
                <Layout>
                  <Outlet />
                </Layout>
              </OnboardingCheck>
            </ProtectedRoute>
          )
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="accounts/:id" element={<AccountDetail />} />
        <Route path="journal" element={<Journal />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="bank-accounts" element={<BankAccounts />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="tax-codes" element={<TaxCodes />} />
        <Route path="reports/trial-balance" element={<TrialBalancePage />} />
        <Route path="reports/balance-sheet" element={<BalanceSheet />} />
        <Route path="reports/profit-loss" element={<ProfitLoss />} />
        <Route path="reports/tax-summary" element={<TaxSummary />} />
        <Route path="reports/inventory-valuation" element={<InventoryValuationPage />} />
        <Route path="organizations/:id/settings" element={<OrganizationSettings />}>
          <Route index element={<Navigate to="members" replace />} />
          <Route path="members" element={<OrganizationMembers />} />
        <Route path="tax-codes" element={<TaxCodes />} />
        <Route path="gfi-codes" element={<GFICodes />} />
          <Route path="integrations" element={<IntegrationSettings />} />
          <Route path="financial" element={<FinancialSettings />} />
          <Route path="audit-log" element={<AuditLog />} />
        </Route>
        <Route path="settings">
          <Route index element={<UserSettings />} />
          <Route path="profile" element={<UserSettings />} />
          <Route path="integrations/shopify" element={<IntegrationSettings />} />
        </Route>
      </Route>

      {/* Legacy routes redirect to /app */}
      <Route path="/accounts" element={<Navigate to="/app/accounts" replace />} />
      <Route path="/accounts/:id" element={<Navigate to="/app/accounts/:id" replace />} />
      <Route path="/journal" element={<Navigate to="/app/journal" replace />} />
      <Route path="/transactions" element={<Navigate to="/app/transactions" replace />} />
      <Route path="/reconciliation" element={<Navigate to="/app/reconciliation" replace />} />
      <Route path="/bank-accounts" element={<Navigate to="/app/bank-accounts" replace />} />
      <Route path="/inventory" element={<Navigate to="/app/inventory" replace />} />
      <Route path="/tax-codes" element={<Navigate to="/app/tax-codes" replace />} />
      <Route path="/gfi-codes" element={<Navigate to="/app/gfi-codes" replace />} />
      <Route path="/reports/*" element={<Navigate to="/app/reports/*" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  console.log('[App] Initializing app');
  return (
    <BrowserRouter>
      <CognitoAuthProvider>
        <OrganizationProvider>
          <AppRoutes />
        </OrganizationProvider>
      </CognitoAuthProvider>
    </BrowserRouter>
  );
}